import React from 'react';
import { useLandingPageData } from '../../context/LandingPageContext';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from '../../i18n/translations';
import { Star, Quote } from 'lucide-react';

const Testimonials = ({ onOpenConsultation }) => {
  const { testimonials } = useLandingPageData();
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  
  // Comprehensive testimonial translations for all pages
  const testimonialTranslations = {
    // Main page testimonials
    'Nach meiner Haartransplantation bei Dion Hair Clinic fühle ich mich wie neugeboren. Das Ergebnis ist unglaublich natürlich und das Team war während des gesamten Prozesses sehr professionell.': {
      en: 'After my hair transplant at Dion Hair Clinic, I feel like a new person. The result is incredibly natural and the team was very professional throughout the entire process.'
    },
    'Von der ersten Beratung bis zur Nachsorge - alles war perfekt organisiert. Mein Selbstbewusstsein ist zurück!': {
      en: 'From the first consultation to aftercare - everything was perfectly organized. My confidence is back!'
    },
    'Ich war anfangs skeptisch, aber die Ergebnisse sprechen für sich. Die Haarlinie sieht absolut natürlich aus. Vielen Dank an das gesamte Team!': {
      en: 'I was skeptical at first, but the results speak for themselves. The hairline looks absolutely natural. Thank you to the entire team!'
    },
    
    // Eyebrow page testimonials
    'Nach Jahren des Überzupfens hatte ich kaum noch Augenbrauen. Jetzt habe ich endlich wieder volle, natürliche Brauen. Ein Traum!': {
      en: 'After years of over-plucking, I barely had any eyebrows left. Now I finally have full, natural brows again. A dream come true!'
    },
    'Ich hatte eine Narbe in der Augenbraue nach einem Unfall. Die Transplantation hat sie komplett verdeckt. Fantastisches Ergebnis!': {
      en: 'I had a scar in my eyebrow after an accident. The transplant completely covered it. Fantastic result!'
    },
    'Microblading musste ich alle paar Jahre erneuern. Die Transplantation war die beste Entscheidung - natürlich und dauerhaft!': {
      en: 'I had to renew my microblading every few years. The transplant was the best decision - natural and permanent!'
    },
    
    // Beard page testimonials
    'Endlich habe ich den Vollbart, den ich mir immer gewünscht habe. Die Lücken sind komplett geschlossen und das Ergebnis sieht völlig natürlich aus.': {
      en: 'I finally have the full beard I always wanted. The gaps are completely closed and the result looks completely natural.'
    },
    'Nach einer Narbe durch einen Unfall war mein Bart ungleichmäßig. Die Transplantation hat alles ausgeglichen - niemand sieht mehr etwas davon.': {
      en: 'After a scar from an accident, my beard was uneven. The transplant balanced everything out - nobody can see it anymore.'
    },
    'Genetisch hatte ich nur schwachen Bartwuchs. Jetzt trage ich stolz einen vollen Bart und fühle mich endlich männlich.': {
      en: 'Genetically, I only had weak beard growth. Now I proudly wear a full beard and finally feel masculine.'
    },
  };

  const getTranslatedText = (text) => {
    if (language === 'de') return text;
    return testimonialTranslations[text]?.en || text;
  };
  
  return (
    <section className={`py-24 relative overflow-hidden transition-colors duration-300 ${
      isDark ? 'bg-[#0f0f0f]' : 'bg-[#f8f9fa]'
    }`}>
      {/* Background */}
      <div className="absolute inset-0">
        <div className={`absolute top-1/2 right-0 w-[500px] h-[500px] rounded-full blur-[80px] ${
          isDark ? 'bg-[#7BA7C2]/5' : 'bg-[#5A8BA6]/5'
        }`} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium mb-4 ${
            isDark ? 'bg-[#7BA7C2]/10 text-[#7BA7C2]' : 'bg-[#5A8BA6]/10 text-[#5A8BA6]'
          }`}>
            {t('testimonials.sectionBadge')}
          </span>
          <h2 className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {language === 'de' ? 'Was unsere ' : 'What Our '}
            <span className="bg-gradient-to-r from-[#7BA7C2] to-[#9BBFD9] bg-clip-text text-transparent">
              {language === 'de' ? 'Patienten' : 'Patients'}
            </span>
            {language === 'de' ? ' sagen' : ' Say'}
          </h2>
          <p className={`max-w-2xl mx-auto text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {t('testimonials.sectionDescription')}
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => {
            // Get translated testimonial text
            const translatedText = getTranslatedText(testimonial.text);
            
            return (
              <div
                key={testimonial.id}
                className={`group relative p-8 rounded-2xl border transition-all duration-500 card-lift ${
                  isDark 
                    ? 'bg-gradient-to-br from-white/[0.03] to-transparent border-white/5 hover:border-[#7BA7C2]/30'
                    : 'bg-white border-gray-200 hover:border-[#5A8BA6]/30'
                }`}
              >
                {/* Quote Icon */}
                <div className={`absolute top-8 right-8 opacity-10 group-hover:opacity-20 transition-opacity duration-500 ${
                  isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'
                }`}>
                  <Quote className="w-16 h-16" />
                </div>

                {/* Rating */}
                <div className="flex gap-1 mb-6">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                  ))}
                </div>

                {/* Text */}
                <p className={`mb-8 leading-relaxed relative z-10 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  "{translatedText}"
                </p>

                {/* Author */}
                <div className="flex items-center gap-4">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    loading="lazy"
                    className={`w-12 h-12 rounded-full object-cover ring-2 ${
                      isDark ? 'ring-[#7BA7C2]/20' : 'ring-[#5A8BA6]/20'
                    }`}
                  />
                  <div>
                    <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{testimonial.name}</p>
                    <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{testimonial.location}</p>
                  </div>
                </div>

                {/* Treatment Badge */}
                <div className={`mt-6 pt-6 border-t ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                  <span className={`text-sm ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`}>{testimonial.treatment}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Trust Indicators */}
        <div className="mt-16 flex flex-wrap items-center justify-center gap-8">
          <div className="flex items-center gap-2">
            <div className="flex -space-x-2">
              {testimonials.slice(0, 3).map((t, i) => (
                <img
                  key={i}
                  src={t.image}
                  alt="Zufriedener Patient"
                  loading="lazy"
                  className={`w-8 h-8 rounded-full border-2 object-cover ${
                    isDark ? 'border-[#0a0a0a]' : 'border-white'
                  }`}
                />
              ))}
            </div>
            <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {language === 'de' ? '+5.000 zufriedene Patienten' : '+5,000 satisfied patients'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-4 h-4 text-yellow-500 fill-yellow-500" />
              ))}
            </div>
            <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {language === 'de' ? '4.9/5 bei Google' : '4.9/5 on Google'}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;